import { theme as antTheme } from "antd";
import type { ThemeConfig } from "antd";

import type { ResolvedTheme } from "./theme";

/** The accent when the school has not chosen its own colour. */
export const DEFAULT_PRIMARY = "#3366ff";

export const FONT_FAMILY =
  '"Inter Variable", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Microsoft YaHei", sans-serif';

/**
 * Portal-design knobs antd needs too (font, base size, corner radius, v2: primary-button colour, contrast,
 * motion); defaults = the portal's own look.
 */
export type AntLook = {
  fontFamily: string;
  fontSize: number;
  radius: "sharp" | "default" | "rounded";
  /** design `brand.buttonColor`: primary buttons in their own colour (null = the accent). */
  buttonColor?: string | null;
  /** design `accessibility.highContrast`: stronger secondary text and borders. */
  highContrast?: boolean;
  /** design `accessibility.reduceMotion`: antd's own animations off. */
  reduceMotion?: boolean;
};
export const DEFAULT_LOOK: AntLook = { fontFamily: FONT_FAMILY, fontSize: 15, radius: "default" };

/** The high-contrast twins of the text/border greys; restated in `styles/tokens.scss` (`[data-contrast="high"]`). */
export const HIGH_CONTRAST: Record<ResolvedTheme, { text2: string; text3: string; border: string }> = {
  light: { text2: "#363d4a", text3: "#4f5869", border: "#aeb5c2" },
  dark: { text2: "#d3d7df", text3: "#b4bac6", border: "#555d6b" },
};

/** antd radii per portal radius; the `--axl-radius*` twins live in `shared/lib/design/apply-design.ts`. */
const ANT_RADII: Record<AntLook["radius"], { base: number; lg: number; sm: number }> = {
  sharp: { base: 4, lg: 6, sm: 3 },
  default: { base: 10, lg: 14, sm: 8 },
  rounded: { base: 16, lg: 20, sm: 12 },
};

/**
 * Surfaces of both themes. Restated in `styles/tokens.scss` (`--axl-bg`, `--axl-surface`, …) — keep the
 * two in step: antd components and our own SCSS must sit on the same greys.
 */
export const SURFACES: Record<ResolvedTheme, { layout: string; container: string; elevated: string; border: string }> = {
  light: { layout: "#f5f6f8", container: "#ffffff", elevated: "#ffffff", border: "#e3e6ec" },
  dark: { layout: "#0c0e12", container: "#15181e", elevated: "#1d2129", border: "#2a2f39" },
};

/** css-var scope of the root provider; the static-API holder needs its own (see `staticAntTheme.tsx`). */
export const CSS_VAR_KEY = "school";
export const STATIC_CSS_VAR_KEY = "school-static";

const isHexColor = (value: string | null | undefined): value is string =>
  !!value && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());

export function buildAntThemeConfig(
  resolved: ResolvedTheme,
  primary?: string | null,
  cssVarKey: string = CSS_VAR_KEY,
  look: AntLook = DEFAULT_LOOK,
): ThemeConfig {
  const isDark = resolved === "dark";
  const surfaces = SURFACES[resolved];
  const radii = ANT_RADII[look.radius] ?? ANT_RADII.default;
  const hc = look.highContrast ? HIGH_CONTRAST[resolved] : null;
  const button = isHexColor(look.buttonColor) ? look.buttonColor.trim() : null;

  return {
    algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    cssVar: { key: cssVarKey },
    hashed: false,
    token: {
      ...(look.reduceMotion ? { motion: false } : {}),
      colorPrimary: isHexColor(primary) ? primary.trim() : DEFAULT_PRIMARY,
      colorInfo: isHexColor(primary) ? primary.trim() : DEFAULT_PRIMARY,
      colorSuccess: "#16a34a",
      colorWarning: "#f59e0b",
      colorError: "#e5484d",
      colorBgLayout: surfaces.layout,
      colorBgContainer: surfaces.container,
      colorBgElevated: surfaces.elevated,
      colorBorder: hc?.border ?? surfaces.border,
      colorBorderSecondary: hc?.border ?? surfaces.border,
      colorText: isDark ? "#e8eaef" : "#151922",
      colorTextSecondary: hc?.text2 ?? (isDark ? "#a4abb9" : "#5b6474"),
      colorTextTertiary: hc?.text3 ?? (isDark ? "#7d8595" : "#8a93a3"),
      fontFamily: look.fontFamily,
      fontSize: look.fontSize,
      borderRadius: radii.base,
      borderRadiusLG: radii.lg,
      borderRadiusSM: radii.sm,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      motionDurationMid: "0.2s",
      wireframe: false,
      // depth (docs/features/ui-depth-styleguide.md): the `--axl-*` shadows switch with the theme on <html>, so antd
      // takes them as css variables, not as colours of its own
      boxShadow: "var(--axl-shadow-panel)",
      boxShadowSecondary: "var(--axl-shadow-3)",
      boxShadowTertiary: "var(--axl-shadow-2)",
    },
    components: {
      Button: {
        // 500, not 600: bold labels made the filled buttons look heavy (owner 2026-10-26)
        fontWeight: 500,
        // the sheen, hover lift and press are in styles/depth.scss
        primaryShadow: "var(--axl-shadow-btn)",
        defaultShadow: "var(--axl-shadow-1)",
        dangerShadow: "var(--axl-shadow-btn)",
        // own primary-button colour: antd re-derives hover/active from it for buttons only
        ...(button ? { colorPrimary: button, algorithm: true } : {}),
      },
      Input: { activeShadow: "0 0 0 3px var(--axl-ring)" },
      InputNumber: { activeShadow: "0 0 0 3px var(--axl-ring)" },
      Select: { activeOutlineColor: "var(--axl-ring)" },
      Card: { headerFontSize: 17 },
      Modal: { titleFontSize: 18 },
      Tabs: { titleFontSize: 15 },
      Menu: { itemBorderRadius: radii.base, activeBarHeight: 0 },
      Layout: { headerBg: surfaces.container, bodyBg: surfaces.layout, siderBg: surfaces.container },
    },
  };
}
